import { forwardRef, useState, type ButtonHTMLAttributes, type MouseEventHandler, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Botão sem os estilos do design system: o visual vem das classes `cm-ef-*`
 * (editor-forense.css, fora das camadas do Tailwind, então vence as
 * utilitárias). `size={null}` não injeta altura nem padding.
 */
export const BotaoNu = forwardRef<HTMLButtonElement, ButtonProps>(function BotaoNu({ type = 'button', ...props }, ref) {
  return <Button ref={ref} type={type} variant="unstyled" size={null} {...props} />;
});

/** O que a tooltip mostra: o nome, os atalhos do Word e, quando ajuda, uma linha. */
export interface Dica {
  nome: string;
  /** "Ctrl+N" ou vários: ["Ctrl+N", "Ctrl+B"]. */
  atalho?: string | string[];
  desc?: string;
}

function ConteudoDica({ dica }: { dica: Dica }) {
  const atalhos = dica.atalho ? (Array.isArray(dica.atalho) ? dica.atalho : [dica.atalho]) : [];
  return (
    <span className="cm-ef-dica">
      <span className="cm-ef-dica-linha">
        <strong>{dica.nome}</strong>
        {atalhos.length > 0 && (
          <span className="cm-ef-dica-atalhos">
            {atalhos.map((a, i) => (
              <span key={a}>{i > 0 && <span className="cm-ef-dica-ou">ou</span>}<kbd>{a}</kbd></span>
            ))}
          </span>
        )}
      </span>
      {dica.desc && <span className="cm-ef-dica-desc">{dica.desc}</span>}
    </span>
  );
}

export interface BotaoProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'className' | 'id' | 'children'> {
  dica: Dica;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  ativo?: boolean;
  desativado?: boolean;
  /** Mostra o texto ao lado do ícone. */
  rotulo?: ReactNode;
  icone?: ReactNode;
  variante?: 'normal' | 'forte' | 'primario' | 'perigo';
  /** Sem tooltip: na barra do celular não há ponteiro, e o toque abriria o balão. */
  semDica?: boolean;
  /** Mantém a tooltip fechada (ex.: com o menu do botão aberto). */
  dicaFechada?: boolean;
  id?: string;
  className?: string;
}

/**
 * Botão da barra. O clique não tira o foco do texto (mousedown preventDefault):
 * negritar não pode jogar o cursor fora da peça.
 */
export const Botao = forwardRef<HTMLButtonElement, BotaoProps>(function Botao(
  { dica, onClick, ativo, desativado, rotulo, icone, variante = 'normal', semDica, dicaFechada, id, className, ...resto },
  ref,
) {
  // Controlada sempre: com o menu do botão aberto, fica fechada.
  const [dicaAberta, setDicaAberta] = useState(false);
  const botao = (
    <BotaoNu
      {...resto}
      ref={ref}
      id={id}
      className={`cm-ef-bt cm-ef-bt-${variante}${ativo ? ' is-ativo' : ''}${className ? ` ${className}` : ''}`}
      aria-label={typeof rotulo === 'string' ? undefined : dica.nome}
      aria-pressed={ativo === undefined ? undefined : ativo}
      disabled={desativado}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {icone}
      {rotulo && <span className="cm-ef-bt-rot">{rotulo}</span>}
    </BotaoNu>
  );
  if (semDica) return botao;
  return (
    <Tooltip open={!dicaFechada && dicaAberta} onOpenChange={setDicaAberta}>
      <TooltipTrigger asChild>{botao}</TooltipTrigger>
      <TooltipContent side="bottom" className="cm-ef-dica-balao">
        <ConteudoDica dica={dica} />
      </TooltipContent>
    </Tooltip>
  );
});

export interface MenuBotaoProps extends Omit<BotaoProps, 'onClick'> {
  children: ReactNode | ((fechar: () => void) => ReactNode);
  largura?: number;
  /** Botão com cara de seletor (estilo, fonte, tamanho). */
  seletor?: boolean;
  alinhar?: 'start' | 'end';
}

// Sem largura, o menu copiaria a do botão que o abriu: no "¶ 1,25" (55 px)
// saía estreito e cortava as opções. Menu tem largura de menu, não de botão.
const LARGURA_MENU = 248;

/** Botão que abre um menu ancorado. */
export function MenuBotao({ children, largura, seletor, alinhar = 'start', ...botao }: MenuBotaoProps) {
  const [aberto, setAberto] = useState(false);
  const fechar = () => setAberto(false);
  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <span className="cm-ef-ancora">
        <PopoverTrigger asChild>
          <Botao
            {...botao}
            className={`${seletor ? 'cm-ef-sel' : ''} ${botao.className ?? ''}`.trim()}
            dicaFechada={aberto}
            ativo={botao.ativo ?? (aberto || undefined)}
            rotulo={<>{botao.rotulo}<ChevronDown className="cm-ef-seta" aria-hidden /></>}
          />
        </PopoverTrigger>
      </span>
      <PopoverContent
        align={alinhar}
        className="cm-ef-menu z-[1500] w-auto p-0"
        style={{ width: largura ?? LARGURA_MENU }}
        // O foco fica no texto: a seleção continua visível e o comando do menu volta a ela.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="cm-ef-menu-corpo" role="menu" onMouseDown={(e) => { if (!(e.target as HTMLElement).closest('input, select, textarea')) e.preventDefault(); }}>
          {typeof children === 'function' ? children(fechar) : children}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ItemMenu({ children, onClick, ativo, atalho, detalhe, marcado, className }: {
  children: ReactNode; onClick: () => void; ativo?: boolean; atalho?: string; detalhe?: ReactNode;
  /** Item de liga/desliga. */
  marcado?: boolean;
  className?: string;
}) {
  return (
    <BotaoNu
      role={marcado === undefined ? 'menuitem' : 'menuitemcheckbox'}
      aria-checked={marcado}
      className={`cm-ef-op${ativo ? ' is-ativo' : ''}${marcado !== undefined ? ' cm-ef-op-check' : ''}${className ? ` ${className}` : ''}`}
      onClick={onClick}
    >
      {marcado !== undefined && <span className={`cm-ef-caixa${marcado ? ' is-marcada' : ''}`} aria-hidden />}
      <span className="cm-ef-op-txt">{children}{detalhe && <span className="cm-ef-op-det">{detalhe}</span>}</span>
      {atalho && <kbd className="cm-ef-kbd">{atalho}</kbd>}
    </BotaoNu>
  );
}

export const TituloMenu = ({ children }: { children: ReactNode }) => <div className="cm-ef-menu-titulo">{children}</div>;
export const SepMenu = () => <div className="cm-ef-menu-sep" role="separator" />;
export const TextoMenu = ({ children }: { children: ReactNode }) => <p className="cm-ef-menu-texto">{children}</p>;
export const Grupo = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={`cm-ef-grupo${className ? ` ${className}` : ''}`}>{children}</div>
);

/** Grade de linhas × colunas para inserir tabela. */
export function GradeTabela({ aoEscolher }: { aoEscolher: (linhas: number, colunas: number) => void }) {
  const [sob, setSob] = useState<[number, number]>([0, 0]);
  return (
    <div>
      <div className="cm-ef-grade" onMouseLeave={() => setSob([0, 0])}>
        {Array.from({ length: 6 * 8 }, (_, i) => {
          const l = Math.floor(i / 8) + 1;
          const c = (i % 8) + 1;
          return (
            <BotaoNu
              key={i}
              aria-label={`${l} linhas por ${c} colunas`}
              className={`cm-ef-grade-cel${l <= sob[0] && c <= sob[1] ? ' is-on' : ''}`}
              onMouseEnter={() => setSob([l, c])}
              onFocus={() => setSob([l, c])}
              onClick={() => aoEscolher(l, c)}
            />
          );
        })}
      </div>
      <div className="cm-ef-grade-rot">{sob[0] ? `${sob[0]} × ${sob[1]} (linhas × colunas)` : 'Passe o ponteiro para escolher'}</div>
    </div>
  );
}
